import * as React from 'react';
import { useContext } from 'react';

import type { QueryRosterByOrganizationIdQuery } from 'bundles/unified-home-common/queries/__generated__/QueryRostersByOrganizationId';
import type { ThirdPartyOrganizationFragment } from 'bundles/unified-home-common/queries/__generated__/ThirdPartyOrganizationQuery';

type OrgRosterElement = NonNullable<
  NonNullable<QueryRosterByOrganizationIdQuery['EnterpriseRosters']>['queryRosterByOrganizationId']
>['elements'][number];

type OrgContracts = OrgRosterElement['contract'][];

type ThirdPartyOrganizationContextType = {
  orgSlug?: string;
  thirdPartyOrganization?: ThirdPartyOrganizationFragment;
  hasThirdPartyOrgError?: boolean;
  isThirdPartyOrgLoading?: boolean;
  orgContracts?: OrgContracts;
  isOrgContractsLoading?: boolean;
};

const EnterpriseThirdPartyOrganizationContext = React.createContext<ThirdPartyOrganizationContextType | undefined>(
  undefined
);

/**
 * @description Hook to get the third party organization general information
 */
export const useThirdPartyOrganization = () => {
  const context = useContext(EnterpriseThirdPartyOrganizationContext);

  return context?.thirdPartyOrganization;
};

/**
 * @description Hook to get the third party organization loading state
 */
export const useIsThirdPartyOrgLoading = () => {
  const context = useContext(EnterpriseThirdPartyOrganizationContext);

  return context?.isThirdPartyOrgLoading;
};

/**
 * @description Hook to get the third party organization error state
 */
export const useHasThirdPartyOrgError = () => {
  const context = useContext(EnterpriseThirdPartyOrganizationContext);

  return context?.hasThirdPartyOrgError;
};
/**
 * @description Hook to get the org slug from the context
 */
export const useOrgSlug = () => {
  const context = useContext(EnterpriseThirdPartyOrganizationContext);

  return context?.orgSlug;
};

/**
 * @description Hook to get the org contracts from the context
 */
export const useOrgContracts = () => {
  const context = useContext(EnterpriseThirdPartyOrganizationContext);

  return context?.orgContracts;
};

/**
 * @description Hook to get the org contracts loading state
 */
export const useIsOrgContractsLoading = () => {
  const context = useContext(EnterpriseThirdPartyOrganizationContext);

  return context?.isOrgContractsLoading;
};

export default EnterpriseThirdPartyOrganizationContext;
